import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Gift, Sparkles, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "../components/ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public one-product preview page — the link a manager copies from the
 * admin catalog and pastes into WhatsApp. Works without login.
 *
 * URL: `/preview/product/:id[?ref=<manager_id>]`
 *   • `ref` = manager who shared the link. Persisted in sessionStorage so
 *     the subsequent signup flow can attribute the new client to them.
 *   • Shows full card (photo carousel + name + brand + price + sizes +
 *     description) with a sticky bottom CTA that promises 10.000 ₸
 *     welcome bonus on registration.
 */
export default function PublicProductPreview() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [state, setState] = useState({ loading: true, error: "" });

  useEffect(() => {
    // Stash the referring manager id so the OTP-register endpoint can
    // consume it as an attribution hint. (Server-side we don't yet act on
    // `ref` alone — only on `share_token` — but this leaves the door open
    // for future manager-attribution on single-product links.)
    const ref = sp.get("ref");
    if (ref) {
      try { sessionStorage.setItem("preview_ref_manager", ref); } catch { /* noop */ }
    }
    axios.get(`${API}/products/${id}`)
      .then(r => { setProduct(r.data); setState({ loading: false, error: "" }); })
      .catch(e => setState({
        loading: false,
        error: e.response?.data?.detail || "Товар не найден",
      }));
  }, [id, sp]);

  const goSignup = () => navigate("/auth");

  if (state.loading) {
    return (
      <div style={centerScreen}>
        <Loader2 size={28} className="animate-spin" style={{ color: "var(--ang-gold)" }} />
      </div>
    );
  }
  if (state.error || !product) {
    return (
      <div style={{ ...centerScreen, flexDirection: "column", gap: 14 }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "var(--ang-bg)" }}>Angiolini</div>
        <div style={{ fontFamily: "Manrope", color: "#7a7a79", fontSize: 14 }}>{state.error}</div>
        <button onClick={goSignup} style={goldBtn}>Открыть Angiolini</button>
      </div>
    );
  }

  const images = product.images || [];
  const price = Number(product.price || 0).toLocaleString("ru-RU");
  const oldP = product.old_price ? Number(product.old_price).toLocaleString("ru-RU") : null;

  return (
    <div data-testid="public-product-preview"
      style={{ minHeight: "100vh", background: "#faf9f7", paddingBottom: 110 }}>

      <div style={{ background: "var(--ang-bg)", color: "var(--ang-text)", padding: "20px",
                    textAlign: "center", fontFamily: "Cormorant Garamond",
                    fontSize: 26, letterSpacing: 3 }}>
        ANGIOLINI
      </div>

      {/* Image carousel */}
      <div style={{ position: "relative", width: "100%", background: "#f5f5f4",
                    aspectRatio: "3/4", maxWidth: 620, margin: "0 auto" }}>
        {images.length > 0 ? (
          <img key={imgIdx} src={resolveImageUrl(images[imgIdx])} alt={product.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        color: "var(--ang-text-muted)", fontFamily: "Manrope", fontSize: 13 }}>
            нет фото
          </div>
        )}
        {images.length > 1 && (
          <>
            <button onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)}
              style={carouselBtn("left")}><ChevronLeft size={18} /></button>
            <button onClick={() => setImgIdx((imgIdx + 1) % images.length)}
              style={carouselBtn("right")}><ChevronRight size={18} /></button>
            <div style={{ position: "absolute", bottom: 10, left: "50%",
                          transform: "translateX(-50%)",
                          background: "rgba(0,0,0,0.55)", color: "var(--ang-text)",
                          padding: "3px 10px", borderRadius: 999,
                          fontFamily: "Manrope", fontSize: 11 }}>
              {imgIdx + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {/* Details */}
      <div style={{ padding: "22px 20px 20px", maxWidth: 620, margin: "0 auto" }}>
        {product.brand && (
          <div style={{ fontFamily: "Manrope", fontSize: 11, color: "var(--ang-text-muted)",
                        letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>
            {product.brand}
          </div>
        )}
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 28, fontWeight: 500,
                      color: "var(--ang-bg)", lineHeight: 1.2, marginBottom: 12 }}>
          {product.name}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
          <span style={{ fontFamily: "Cormorant Garamond", fontSize: 30, fontWeight: 600, color: "var(--ang-gold)" }}>
            {price} ₸
          </span>
          {oldP && (
            <span style={{ fontFamily: "Manrope", fontSize: 13, color: "var(--ang-text-muted)",
                           textDecoration: "line-through" }}>{oldP} ₸</span>
          )}
        </div>
        {product.sizes?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 11, color: "var(--ang-text-muted)",
                          letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
              Доступные размеры
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {product.sizes.map(s => (
                <span key={s} style={{ padding: "6px 12px", border: "1px solid #d4d4d4",
                                        borderRadius: 4, fontFamily: "Manrope", fontSize: 13,
                                        color: "var(--ang-bg)", background: "white" }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
        {product.description && (
          <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#374151",
                        lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {product.description}
          </div>
        )}
      </div>

      {/* Sticky CTA */}
      <div data-testid="preview-cta"
        style={{ position: "fixed", left: 0, right: 0, bottom: 0,
                 background: "linear-gradient(180deg, rgba(250,249,247,0) 0%, #faf9f7 25%)",
                 padding: "14px 16px 18px", zIndex: 50,
                 borderTop: "1px solid var(--ang-gold-soft)" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10,
                        justifyContent: "center", marginBottom: 8,
                        fontFamily: "Manrope", fontSize: 12, color: "#5a5a59" }}>
            <Sparkles size={14} style={{ color: "var(--ang-gold)" }} />
            Хотите провести виртуальную примерку?
          </div>
          <button data-testid="preview-cta-signup" onClick={goSignup}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              background: "linear-gradient(135deg, var(--ang-gold) 0%, var(--ang-gold-2) 100%)",
              border: "none", borderRadius: 8, padding: "14px 20px",
              color: "var(--ang-bg)", fontFamily: "Manrope", fontSize: 14, fontWeight: 700,
              cursor: "pointer", boxShadow: "0 6px 20px var(--ang-gold-line)",
            }}>
            <Gift size={18} /> Зарегистрироваться и получить 10 000 ₸
          </button>
          <div style={{ textAlign: "center", fontFamily: "Manrope", fontSize: 10,
                        color: "var(--ang-text-muted)", marginTop: 6 }}>
            Полный каталог, ИИ-примерка и запись на консультацию
          </div>
        </div>
      </div>
    </div>
  );
}

const centerScreen = { minHeight: "100vh", display: "flex", alignItems: "center",
                       justifyContent: "center", background: "#faf9f7" };
const goldBtn = {
  background: "linear-gradient(135deg, var(--ang-gold), var(--ang-gold-2))", color: "var(--ang-bg)",
  border: "none", borderRadius: 8, padding: "10px 18px",
  fontFamily: "Manrope", fontSize: 13, fontWeight: 700, cursor: "pointer",
};
const carouselBtn = (side) => ({
  position: "absolute", [side]: 10, top: "50%", transform: "translateY(-50%)",
  background: "var(--ang-text-soft)", border: "none", borderRadius: "50%",
  width: 34, height: 34, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  color: "var(--ang-bg)", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
});
